import React, { useState, useContext, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { fields } from 'src/components/utils/fields'
import { BufferService, LoadingContext, M_Button, M_ElementDrawer } from 'src/M_Components'
import { Receipt } from '@mui/icons-material'
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Table, TableBody, TableCell, TableRow } from '@mui/material'

export const DemurrageCalculateCost = ({ open, setOpen, selectedDemurrage, onSuccess }) => {
    const { t } = useTranslation()
    const ctx = useContext(LoadingContext)
    const [data, setData] = useState({})
    const [resultOpen, setResultOpen] = useState(false)
    const [resultData, setResultData] = useState(null)
    const [loading, setLoading] = useState(false)

    const demurrageControlService = new BufferService('DemurrageControl', 'secondary')
    const accountService = new BufferService('Account')

    const controlTypeOptions = [
        { label: t('Terminal'), value: 'Terminal' },
        { label: t('Demurrage'), value: 'Demurrage' },
        { label: t('Commission'), value: 'Commission' },
        { label: t('Other'), value: 'Other' },
        { label: t('Value'), value: 'Value' }
    ]

    const controlDetailsFields = [
        { display: t('Control Date'), code: 'controlDate', type: 'date' },
        { type: 'separator' },
        { display: t('Holidays'), code: 'holidays', type: 'disabled' },
        { display: t('Invoice Code'), required: true, code: 'invoiceCode', type: 'foreignKey', entity: 'Account', filterField: 'AccountCode', labelFields: ['AccountCode'], fields: [{ key: 'AccountCode', value: 'invoiceCode' }, { key: 'AccountName', value: 'invoiceName' }, { key: 'Id', value: 'invoiceCodeId' }] },
        { display: t('Invoice Name'), code: 'invoiceName', type: 'disabled' },
        { display: t('Cost Discount'), code: 'DemurrageCostDiscount', type: 'disabled' },
        { display: t('Cost'), code: 'DemurrageCost', type: 'disabled' },
        { display: t('Control Type'), code: 'Type', type: 'select', options: controlTypeOptions },
        { display: t('Comments'), code: 'Comments', type: 'disabled', fullWidth: true }
    ]

    const excludedFields = ['BookingID', 'BookingTypeID', 'ExternalId', 'CreatedAt', 'UpdatedAt', 'Id', 'InvoiceID']

    const formatResultValue = (key, value) => {
        if (key === 'Date' && value) return value.split('T')[0]
        if (value === null || value === undefined || value === '') return '-'
        return String(value)
    }

    useEffect(() => {
        if (open && selectedDemurrage?.DemurrageControlId) {
            fetchDemurrageControlData(selectedDemurrage.DemurrageControlId)
        } else if (open && selectedDemurrage) {
            setData({
                holidays: selectedDemurrage.Holidays || 0,
                DemurrageCost: selectedDemurrage.DemurrageControlCost || 0,
                DemurrageCostDiscount: 0,
                Type: selectedDemurrage.DemurrageControlType || 'Demurrage',
                Comments: '',
                invoiceCode: selectedDemurrage.InvoiceID || '',
                invoiceName: selectedDemurrage.InvoiceName || ''
            })
        }
    }, [open, selectedDemurrage])

    const fetchDemurrageControlData = async (demurrageControlId) => {
        setLoading(true)
        try {
            const response = await demurrageControlService.getId(demurrageControlId)
            if (response.name === 'AxiosError') {
                ctx.toastMessages('error', '', t('Failed to load demurrage control data'))
                return
            }

            let invoiceCode = ''
            let invoiceName = ''
            if (response.InvoiceID) {
                const accountResponse = await accountService.getId(response.InvoiceID)
                if (accountResponse.name !== 'AxiosError') {
                    invoiceCode = accountResponse.AccountCode || ''
                    invoiceName = accountResponse.AccountName || ''
                }
            }

            setData({
                controlDate: '',
                holidays: response.Holidays || 0,
                DemurrageCost: response.DemurrageCost || 0,
                DemurrageCostDiscount: response.DemurrageCostDiscount || 0,
                Type: response.Type || 'Demurrage',
                Comments: response.Comments || '',
                invoiceCodeId: response.InvoiceID || '',
                invoiceCode,
                invoiceName,
                InvoiceKey: response.InvoiceKey || '',
                InvoiceCompany: response.InvoiceCompany || ''
            })
        } catch (error) {
            console.error('Error fetching demurrage control:', error)
            ctx.toastMessages('error', '', t('Failed to load demurrage control data'))
        } finally {
            setLoading(false)
        }
    }

    const handleSave = () => {
        if (!selectedDemurrage?.DemurrageControlId) {
            ctx.toastMessages('error', '', t('No DemurrageControl associated with this demurrage'))
            return
        }

        ctx.setIsLoad(true)

        const calculateCostService = new BufferService(
            `DemurrageControl/${selectedDemurrage.DemurrageControlId}/CalculateCosts/${selectedDemurrage.Id}${data.controlDate ? `?billingDate=${data.controlDate}` : ''}`,
            'secondary'
        )

        calculateCostService.post([]).then(response => {
            ctx.setIsLoad(false)
            if (response.name === 'AxiosError') {
                const message = response.response?.data || t('Failed to calculate cost')
                ctx.toastMessages('error', '', message)
                return
            }

            ctx.toastMessages('success', '', t('Cost calculated successfully'))
            if (onSuccess) onSuccess(response)

            setResultData(response)
            setResultOpen(true)
            setOpen(false)
            setData({})
        }).catch(() => {
            ctx.setIsLoad(false)
            ctx.toastMessages('error', '', t('Failed to calculate cost'))
        })
    }

    const handleClose = () => {
        setOpen(false)
        setData({})
    }

    return (
        <>
            <M_ElementDrawer
                open={open}
                setOpen={setOpen}
                onClose={handleClose}
                width="600px"
                element={
                    <div style={{ margin: '0px 5px' }}>
                        {/* Header */}
                        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', margin: '20px 0px' }}>
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                <h2 style={{ color: ctx.secondaryColor, display: 'flex', alignItems: 'center', marginTop: '0px' }}>
                                    <Receipt style={{ marginRight: '10px', fontSize: '30px' }} />
                                    {t('Demurrage Control Date Details')}
                                </h2>
                            </span>
                            <div style={{ border: `1px solid ${ctx.primaryColor}`, width: '90%' }}></div>
                        </div>

                        {/* Loading indicator */}
                        {loading && (
                            <div style={{ textAlign: 'center', padding: '20px' }}>
                                {t('Loading...')}
                            </div>
                        )}

                        {/* Selected Demurrage Info */}
                        {selectedDemurrage && (
                            <div style={{ backgroundColor: '#f8f9fa', padding: '15px', borderRadius: '8px', margin: '20px', border: '1px solid #e9ecef' }}>
                                <div style={{ display: 'grid', gridTemplateColumns: 'auto auto', gap: '15px' }}>
                                    <div><strong>{t('Demurrage ID')}:</strong> {selectedDemurrage.Id}</div>
                                    <div><strong>{t('Status')}:</strong> {selectedDemurrage.Status}</div>
                                    {selectedDemurrage.DemurrageControlId && (
                                        <div><strong>{t('Control ID')}:</strong> {selectedDemurrage.DemurrageControlId}</div>
                                    )}
                                </div>
                            </div>
                        )}

                        {/* Control Details Fields */}
                        <div style={{ margin: '20px', padding: '20px', backgroundColor: '#ffffff', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.05)' }}>
                            <h3 style={{ marginTop: 0, marginBottom: '20px', color: ctx.primaryColor, borderBottom: `2px solid ${ctx.primaryColor}`, paddingBottom: '10px' }}>
                                {t('Demurrage Control Date Details')}
                            </h3>
                            <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: '20px' }}>
                                {controlDetailsFields.map((field, index) => {
                                    if (field.type === 'separator') return <div key={`separator-${index}`} style={{ height: '10px' }} />
                                    if (field.fullWidth) {
                                        return (
                                            <div key={index} style={{ width: '100%' }}>
                                                {fields(field, index, data, setData, false, null, '100%', controlTypeOptions)}
                                            </div>
                                        )
                                    }
                                    return fields(field, index, data, setData, null, null, null, controlTypeOptions)
                                })}
                            </div>
                        </div>

                        {/* Demurrage Control Summary */}
                        {!loading && data.DemurrageCost !== undefined && (
                            <div style={{ margin: '0 20px 20px 20px', padding: '20px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '1px solid #dee2e6' }}>
                                <h3 style={{ marginTop: 0, marginBottom: '16px', color: ctx.primaryColor, borderBottom: `2px solid ${ctx.primaryColor}`, paddingBottom: '10px' }}>
                                    {t('Demurrage Control')}
                                </h3>
                                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px 24px', fontSize: '0.875rem' }}>
                                    {[
                                        { label: t('Control ID'),      value: selectedDemurrage?.DemurrageControlId },
                                        { label: t('Type'),            value: data.Type },
                                        { label: t('Holidays'),        value: data.holidays },
                                        { label: t('Cost'),            value: data.DemurrageCost != null ? parseFloat(data.DemurrageCost).toFixed(2) : '-' },
                                        { label: t('Discount'),        value: data.DemurrageCostDiscount != null ? parseFloat(data.DemurrageCostDiscount).toFixed(2) : '-' },
                                        { label: t('Invoice Company'), value: data.InvoiceCompany },
                                        { label: t('Invoice Key'),     value: data.InvoiceKey },
                                        { label: t('Comments'),        value: data.Comments },
                                    ].map(({ label, value }) => (
                                        <div key={label}>
                                            <span style={{ fontWeight: 600, color: '#495057' }}>{label}: </span>
                                            <span style={{ color: '#212529' }}>{value || '-'}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {/* Buttons */}
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '10px', margin: '30px 20px 20px 20px' }}>
                            <M_Button hoverEffect width={15} label={t("Calculate")} onClick={handleSave} backgroundColor={'var(--color-green)'} labelColor={'var(--color-white)'} disabled={loading} />
                            <M_Button hoverEffect width={15} label={t("Cancel")} onClick={handleClose} backgroundColor={'var(--color-red)'} labelColor={'var(--color-white)'} disabled={loading} />
                        </div>
                    </div>
                }
            />

            {/* Result Popup */}
            <Dialog open={resultOpen} onClose={() => setResultOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: ctx.primaryColor, fontWeight: 'bold', display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <Receipt />
                    {t('Calculation Result')}
                </DialogTitle>
                <DialogContent dividers>
                    {resultData && (
                        <Table size="small">
                            <TableBody>
                                {Object.entries(resultData)
                                    .filter(([key]) => !excludedFields.includes(key))
                                    .map(([key, value]) => (
                                        <TableRow key={key} sx={{ '&:nth-of-type(odd)': { backgroundColor: '#f9f9f9' } }}>
                                            <TableCell sx={{ fontWeight: 'bold', width: '40%', color: ctx.primaryColor }}>
                                                {t(key)}
                                            </TableCell>
                                            <TableCell>
                                                {formatResultValue(key, value)}
                                            </TableCell>
                                        </TableRow>
                                    ))
                                }
                            </TableBody>
                        </Table>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setResultOpen(false)} variant="contained" sx={{ backgroundColor: ctx.primaryColor, color: 'var(--color-white)' }}>
                        {t('Close')}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    )
}